import { useQuery } from "@tanstack/react-query";
import { from, rpc, unwrap, type FileLink } from "@vixio/data";
import type { StatusMap } from "@vixio/ui";

export type ItemKind =
  | "contract" | "amendment" | "deliverable" | "receivable" | "invoice" | "certificate" | "ticket" | "document" | "proposal" | "receipt";

export const KIND_LABEL: Record<ItemKind, string> = {
  contract: "Contrato",
  amendment: "Termo aditivo",
  deliverable: "Entregável",
  receivable: "Fatura",
  invoice: "Nota fiscal",
  certificate: "Certificado",
  ticket: "Chamado",
  document: "Documento",
  proposal: "Proposta",
  receipt: "Recibo",
};

/** Situação de cada tipo de item, em linguagem do cliente. */
export const ITEM_STATUS: StatusMap = {
  signed: { label: "assinado", tone: "ok" },
  terminated: { label: "rescindido", tone: "neutral" },
  expired: { label: "encerrado", tone: "neutral" },
  submitted: { label: "aguardando sua aprovação", tone: "warn" },
  accepted: { label: "aprovado", tone: "ok" },
  rejected: { label: "devolvido para ajuste", tone: "info" },
  open: { label: "em aberto", tone: "info" },
  partial: { label: "pago em parte", tone: "info" },
  paid: { label: "pago", tone: "ok" },
  issued: { label: "emitido", tone: "ok" },
  canceled: { label: "cancelado", tone: "neutral" },
  in_progress: { label: "em andamento", tone: "info" },
  waiting_client: { label: "aguardando você", tone: "warn" },
  resolved: { label: "resolvido", tone: "ok" },
  closed: { label: "fechado", tone: "neutral" },
  shared: { label: "disponível", tone: "ok" },
};

/** Papel da pessoa na empresa cliente (portal 0009). */
export type AccessRole = "responsible" | "finance" | "viewer";

export const ROLE_LABEL: Record<AccessRole, string> = { responsible: "Responsável", finance: "Financeiro", viewer: "Leitura" };

export const ROLE_HINT: Record<AccessRole, string> = {
  responsible: "Vê tudo, aprova entregas e cuida da equipe da empresa.",
  finance: "Faturas, notas, recibos e documentos; envia arquivos; não aprova entregas.",
  viewer: "Acompanha projetos, entregas, documentos e chamados; não vê o financeiro.",
};

export const ROLE_OPTIONS = (Object.keys(ROLE_LABEL) as AccessRole[]).map((r) => ({ value: r, label: ROLE_LABEL[r] }));

export interface PortalItem {
  id: string;
  org_id: string;
  account_id: string | null;
  person_id: string | null;
  kind: ItemKind;
  ref_id: string;
  title: string;
  subtitle: string | null;
  status: string | null;
  amount_cents: number | null;
  doc_code: string | null;
  due_date: string | null;
  needs_action: boolean;
  data: Record<string, unknown>;
  occurred_at: string;
  updated_at: string;
}

export interface PortalMe {
  access_id: string;
  account_id: string;
  can_approve: boolean;
  role: AccessRole;
  can_manage_team: boolean;
  can_see_finance: boolean;
  can_upload: boolean;
  person: { id: string; full_name: string; email: string | null; phone: string | null; company_name: string | null; job_title: string | null };
  accounts: Array<{ id: string; name: string }>;
  pending_term: { id: string; version: string; title: string; body: string; published_at: string } | null;
  accepted_term_version: string | null;
  counts: { to_approve: number; open_receivables: number; open_tickets: number; documents: number };
}

export interface AccountRow {
  id: string;
  org_id: string;
  name: string;
  doc: string | null;
  status: "active" | "suspended";
  notes: string | null;
  created_at: string;
  people: number;
  last_seen_at: string | null;
  items: number;
  pending_actions: number;
}

export interface AccessRow {
  id: string;
  account_id: string;
  account_name: string;
  person_id: string;
  full_name: string;
  email: string;
  user_id: string | null;
  can_approve: boolean;
  granted_at: string;
  revoked_at: string | null;
  revoke_reason: string | null;
  last_seen_at: string | null;
  accepted_version: string | null;
  current_version: string | null;
  role: AccessRole;
  invited_by_name: string | null;
}

export interface TermsRow {
  id: string;
  version: string;
  title: string;
  body: string;
  body_sha256: string;
  published_at: string;
}

export interface ClientPhase {
  id: string;
  position: number;
  name: string;
  status: "pending" | "active" | "done";
  start_date: string | null;
  due_date: string | null;
  done_at: string | null;
  tasks_total: number;
  tasks_done: number;
  late: boolean;
}

export interface ClientProject {
  id: string;
  code: string;
  title: string;
  status: "planning" | "active" | "paused" | "done" | "canceled";
  start_date: string;
  due_date: string | null;
  closed_at: string | null;
  contract_number: string | null;
  progress_pct: number;
  late: boolean;
  phases: ClientPhase[];
  deliverables: Array<{
    id: string;
    title: string;
    status: "pending" | "submitted" | "accepted" | "rejected";
    due_date: string | null;
    phase_id: string | null;
    revision: number;
    accepted_at: string | null;
    acceptance_criteria: string | null;
  }>;
}

export interface ContractDocument {
  id: string;
  item_id: string;
  number: string;
  title: string;
  status: "active" | "expired" | "terminated";
  counterparty_name: string;
  total_cents: number;
  billing_period: "once" | "monthly";
  term_months: number | null;
  start_date: string | null;
  end_date: string | null;
  signed_at: string | null;
  doc_code: string | null;
  versions: Array<{
    version: number;
    kind: "original" | "amendment" | "termination";
    title: string;
    body: string;
    content_sha256: string;
    document_code: string | null;
    signed_at: string | null;
    total_cents: number;
  }>;
}

/** Pagamento da fatura publicado pelo billing (billing.charge_registered). */
export interface PaymentInfo {
  kind?: "boleto" | "pix" | "card";
  provider?: string;
  due_date?: string;
  amount_cents?: number;
  boleto_line?: string;
  pix_copy_paste?: string;
  pdf_url?: string;
}

export const PROJECT_STATUS: StatusMap = {
  planning: { label: "em planejamento", tone: "info" },
  active: { label: "em andamento", tone: "info" },
  paused: { label: "pausado", tone: "warn" },
  done: { label: "concluído", tone: "ok" },
  canceled: { label: "cancelado", tone: "neutral" },
};

export const DELIVERABLE_STATUS: StatusMap = {
  pending: { label: "em produção", tone: "neutral" },
  submitted: { label: "aguardando sua aprovação", tone: "warn" },
  accepted: { label: "aprovado", tone: "ok" },
  rejected: { label: "em ajuste", tone: "info" },
};

export const CONTRACT_STATUS: StatusMap = {
  signed: { label: "vigente", tone: "ok" },
  active: { label: "vigente", tone: "ok" },
  terminated: { label: "rescindido", tone: "neutral" },
  expired: { label: "encerrado", tone: "neutral" },
};

export interface Thread {
  ticket: { id: string; number: string; subject: string; status: string; kind: string; priority: string; opened_at: string; first_response_due: string; resolution_due: string | null; resolved_at: string | null };
  messages: Array<{ author: "client" | "team" | "system"; body: string; at: string }>;
  /** Anexos visíveis (support 0003). */
  attachments?: TicketAttachment[];
}

export interface TicketAttachment {
  id: string;
  name: string;
  content_type: string;
  size_bytes: number;
  author: "client" | "team";
  at: string;
}

/** Arquivo anexado pela equipe a um entregável (delivery 0004). */
export interface DeliverableFile {
  id: string;
  deliverable_id: string;
  project_id: string;
  name: string;
  description: string | null;
  content_type: string;
  size_bytes: number;
  created_at: string;
}

export interface TeamMember {
  access_id: string;
  account_id: string;
  person_id: string;
  full_name: string;
  email: string;
  job_title: string | null;
  role: AccessRole;
  is_me: boolean;
  status: "active" | "invited";
  last_seen_at: string | null;
  granted_at: string;
  invited_by: string | null;
}

export type PdfState = "ready" | "processing" | "failed" | "none";

export interface LibraryItem {
  id: string;
  kind: ItemKind;
  title: string;
  subtitle: string | null;
  status: string | null;
  doc_code: string | null;
  amount_cents: number | null;
  occurred_at: string;
  account_id: string;
  project_id: string | null;
  project_title: string | null;
  pdf: PdfState | null;
  signed: boolean;
  doc_status: "valid" | "revoked" | "superseded" | null;
  type_label: string | null;
}

export interface LibraryProject {
  id: string;
  title: string;
  code: string;
  contract_number: string | null;
}

export interface FileHistoryEntry {
  action: "uploaded" | "downloaded" | "removed";
  at: string;
  actor_kind: "team" | "client";
  actor_name: string | null;
}

export interface PortalFile {
  id: string;
  account_id: string;
  name: string;
  description: string | null;
  direction: "to_client" | "from_client";
  status: "available" | "removed";
  content_type: string;
  size_bytes: number;
  created_at: string;
  confirmed_at: string | null;
  removed_at: string | null;
  project_id: string | null;
  project_title: string | null;
  uploader_kind: "team" | "client";
  uploader_name: string | null;
  mine: boolean;
  history: FileHistoryEntry[];
}

/** Arquivo como a equipe vê (portal.v_files). */
export interface TeamFileRow {
  id: string;
  org_id: string;
  account_id: string;
  account_name: string;
  project_id: string | null;
  direction: "to_client" | "from_client";
  name: string;
  description: string | null;
  content_type: string;
  size_bytes: number;
  status: "pending" | "available" | "removed";
  uploader_kind: "team" | "client";
  uploader_name: string | null;
  created_at: string;
  confirmed_at: string | null;
  removed_at: string | null;
  remove_reason: string | null;
  downloads: number;
  last_download_at: string | null;
}

export type NotificationCategory = "approval" | "finance" | "document" | "ticket" | "file";

export const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  approval: "Entregas para aprovar",
  finance: "Faturas, pagamentos, notas e recibos",
  document: "Contratos e documentos novos",
  ticket: "Respostas nos chamados",
  file: "Arquivos publicados pela equipe",
};

export interface PortalNotification {
  id: string;
  category: NotificationCategory;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  created_at: string;
  read: boolean;
}

export interface NotificationPrefs {
  email_enabled: boolean;
  categories: NotificationCategory[];
  email: string | null;
  role: AccessRole;
  updated_at: string | null;
}

export interface TimelineEntry {
  at: string;
  type: "item" | "approval" | "file" | "terms";
  kind: string;
  status: string | null;
  title: string;
  subtitle?: string | null;
  actor?: string | null;
  item_id?: string;
  file_id?: string;
  amount_cents?: number | null;
}

export interface AccountPreview {
  account: { id: string; name: string; status: string };
  people: Array<{ full_name: string; email: string; role: AccessRole; status: "active" | "invited"; last_seen_at: string | null }>;
  counts: { to_approve: number; open_receivables: number; open_receivables_cents: number; open_tickets: number; documents: number };
  items: Array<{
    id: string; kind: ItemKind; title: string; subtitle: string | null; status: string | null; amount_cents: number | null;
    doc_code: string | null; due_date: string | null; needs_action: boolean; occurred_at: string;
  }>;
  files: Array<{ id: string; name: string; direction: "to_client" | "from_client"; status: string; size_bytes: number; created_at: string }>;
  notifications: Array<{ title: string; category: NotificationCategory; created_at: string }>;
}

export interface UploadTicket {
  file_id: string;
  bucket: string;
  storage_path: string;
  max_bytes: number;
}

/** Tipos aceitos nos arquivos compartilhados (o banco e o bucket conferem de novo). */
export const ACCEPTED_FILES = ".pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.zip";
export const MAX_FILE_BYTES = 25 * 1024 * 1024;

const EXT_TYPES: Record<string, string> = {
  pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", gif: "image/gif",
  txt: "text/plain", csv: "text/csv", doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint", pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  odt: "application/vnd.oasis.opendocument.text", ods: "application/vnd.oasis.opendocument.spreadsheet", zip: "application/zip",
};

/** Tipo do arquivo pela extensão (o navegador às vezes não informa, ou informa outro). */
export function fileContentType(file: { name: string }): string | null {
  const ext = /\.([A-Za-z0-9]{1,8})$/.exec(file.name)?.[1]?.toLowerCase();
  return ext ? (EXT_TYPES[ext] ?? null) : null;
}

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024)).toLocaleString("pt-BR")} KB`;
  return `${(n / 1024 / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "portal.upsert_account": { args: { p_org: U; p_name: string; p_doc?: string | null; p_notes?: string | null; p_status?: string; p_id?: U | null }; returns: U };
    "portal.grant_access": {
      args: { p_account: U; p_person: U; p_can_approve?: boolean; p_invite?: boolean };
      returns: { access_id: U; items_linked: number; invitation?: { invitation_id: U; token: string; expires_at: string } };
    };
    "portal.revoke_access": { args: { p_access: U; p_reason: string }; returns: null };
    "portal.publish_terms": { args: { p_org: U; p_version: string; p_title: string; p_body: string }; returns: U };
    "portal.share_document": {
      args: { p_org: U; p_person: U; p_title: string; p_doc_code?: string | null; p_subtitle?: string | null; p_ref?: U | null };
      returns: U;
    };
    "portal.me": { args: { p_org: U }; returns: PortalMe };
    "portal.accept_terms": { args: { p_term: U }; returns: number };
    "portal.decide_deliverable": { args: { p_item: U; p_decision: "accepted" | "rejected"; p_note?: string | null }; returns: unknown };
    "portal.open_ticket": {
      args: { p_org: U; p_subject: string; p_description: string; p_kind?: string; p_priority?: string; p_account?: U | null };
      returns: U;
    };
    "portal.ticket_thread": { args: { p_item: U }; returns: Thread };
    "portal.reply_ticket": { args: { p_item: U; p_body: string }; returns: null };
    "portal.my_projects": { args: { p_org: U }; returns: ClientProject[] };
    "portal.contract_document": { args: { p_item: U }; returns: ContractDocument };
    "portal.document_file": { args: { p_item: U }; returns: FileLink & { item_id: string } };
    "portal.attach_ticket_reference": { args: { p_item: U; p_url: string; p_label?: string | null }; returns: null };
    "portal.update_my_phone": { args: { p_org: U; p_phone: string | null }; returns: string | null };
    "portal.my_team": { args: { p_org: U }; returns: TeamMember[] };
    "portal.invite_colleague": {
      args: { p_org: U; p_full_name: string; p_email: string; p_role: AccessRole; p_app_url: string; p_account?: U | null };
      returns: { access_id: U; person_id: U; invited: boolean; items_linked: number; expires_at?: string };
    };
    "portal.set_access_role": { args: { p_access: U; p_role: AccessRole }; returns: null };
    "portal.revoke_colleague": { args: { p_access: U; p_reason: string }; returns: null };
    "portal.my_access_log": { args: { p_org: U }; returns: Array<{ at: string; user_agent: string | null; ip_hash: string | null }> };
    "portal.my_library": { args: { p_org: U }; returns: { items: LibraryItem[]; projects: LibraryProject[] } };
    "portal.account_projects": { args: { p_account: U }; returns: Array<{ id: U; title: string; code: string; status: string }> };
    "portal.set_item_project": { args: { p_item: U; p_project: U | null }; returns: null };
    "portal.begin_upload": {
      args: { p_org: U; p_name: string; p_content_type: string; p_size: number; p_project?: U | null; p_description?: string | null; p_account?: U | null };
      returns: UploadTicket;
    };
    "portal.team_begin_upload": {
      args: { p_account: U; p_name: string; p_content_type: string; p_size: number; p_project?: U | null; p_description?: string | null };
      returns: UploadTicket;
    };
    "portal.finish_upload": { args: { p_file: U }; returns: { file_id: U; status: string } };
    "portal.file_link": { args: { p_file: U }; returns: FileLink & { size_bytes: number; content_type: string; file_id: U } };
    "portal.remove_file": { args: { p_file: U; p_reason?: string | null }; returns: null };
    "portal.my_files": { args: { p_org: U }; returns: PortalFile[] };
    "portal.my_deliverable_files": { args: { p_org: U }; returns: DeliverableFile[] };
    "portal.deliverable_file": { args: { p_org: U; p_file: U }; returns: FileLink & { size_bytes: number; content_type: string; file_id: U } };
    "portal.begin_ticket_attachment": {
      args: { p_item: U; p_name: string; p_content_type: string; p_size: number };
      returns: { attachment_id: U; bucket: string; storage_path: string; max_bytes: number };
    };
    "portal.finish_ticket_attachment": { args: { p_item: U; p_attachment: U }; returns: { attachment_id: U; status: string } };
    "portal.ticket_attachment_file": { args: { p_item: U; p_attachment: U }; returns: FileLink & { size_bytes: number; content_type: string } };
    "portal.my_notifications": { args: { p_org: U; p_limit?: number; p_before?: string | null }; returns: { items: PortalNotification[]; unread: number } };
    "portal.notifications_summary": { args: { p_org: U }; returns: { unread: number; latest: PortalNotification[] } };
    "portal.mark_notifications_read": { args: { p_org: U; p_ids?: U[] | null }; returns: number };
    "portal.my_notification_prefs": { args: { p_org: U }; returns: NotificationPrefs };
    "portal.set_notification_prefs": { args: { p_org: U; p_email_enabled: boolean; p_categories: NotificationCategory[] }; returns: null };
    "portal.my_timeline": { args: { p_org: U; p_limit?: number }; returns: TimelineEntry[] };
    "portal.export_my_data": { args: { p_org: U }; returns: Record<string, unknown> };
    "portal.preview_account": { args: { p_account: U }; returns: AccountPreview };
    "portal.invite_access": {
      args: { p_access: U; p_app_url: string };
      returns: { sent: boolean; invited: boolean; link?: string; expires_at?: string };
    };
  }
}

export function usePortalMe(orgId: string) {
  return useQuery({
    queryKey: ["portal", "me", orgId],
    queryFn: () => rpc("portal", "me", { p_org: orgId }),
    retry: false,
    staleTime: 30_000,
  });
}

export function useItems(orgId: string, kinds: ItemKind[]) {
  return useQuery({
    queryKey: ["portal", "items", orgId, kinds.join(",")],
    queryFn: () =>
      unwrap<PortalItem[]>(
        from("portal", "items").select("*").eq("org_id", orgId).in("kind", kinds).not("account_id", "is", null).order("occurred_at", { ascending: false }).limit(500),
      ),
  });
}

export function useLibrary(orgId: string) {
  return useQuery({ queryKey: ["portal", "library", orgId], queryFn: () => rpc("portal", "my_library", { p_org: orgId }) });
}

export function useMyFiles(orgId: string) {
  return useQuery({ queryKey: ["portal", "files", orgId], queryFn: () => rpc("portal", "my_files", { p_org: orgId }) });
}

export function useDeliverableFiles(orgId: string) {
  return useQuery({ queryKey: ["portal", "deliverable_files", orgId], queryFn: () => rpc("portal", "my_deliverable_files", { p_org: orgId }) });
}

export function useTeam(orgId: string) {
  return useQuery({ queryKey: ["portal", "team", orgId], queryFn: () => rpc("portal", "my_team", { p_org: orgId }) });
}

export function useTimeline(orgId: string, limit = 12) {
  return useQuery({
    queryKey: ["portal", "timeline", orgId, limit],
    queryFn: () => rpc("portal", "my_timeline", { p_org: orgId, p_limit: limit }),
  });
}

/** Sino: atualiza a cada minuto e quando a janela volta ao foco. */
export function useNotificationsSummary(orgId: string) {
  return useQuery({
    queryKey: ["portal", "notifications", "summary", orgId],
    queryFn: () => rpc("portal", "notifications_summary", { p_org: orgId }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useProjects(orgId: string) {
  return useQuery({
    queryKey: ["portal", "projects", orgId],
    queryFn: () => rpc("portal", "my_projects", { p_org: orgId }),
  });
}

export function paymentOf(i: PortalItem): PaymentInfo | null {
  const p = i.data?.payment;
  return p && typeof p === "object" ? (p as PaymentInfo) : null;
}

/** Só links https do provedor viram link clicável. */
export function safeHttps(url: string | undefined | null): string | null {
  return url && /^https:\/\/[^\s]+$/i.test(url) ? url : null;
}
